import * as React from 'react'
import { CreditCard, ExternalLink, Globe, Percent, Phone, RefreshCw, ShieldCheck } from 'lucide-react'

import {
  useSettings,
  useUpdateDepositSettings,
  useUpdatePublicProfile,
  useOnboardingStatus,
  useStartOnboarding,
  useSendingDomainStatus,
  useCreateSendingDomain,
  useVerifySendingDomain
} from '@/lib/queries.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { Field, Input, Label } from '@/components/ui/field.jsx'
import { Select } from '@/components/ui/select.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { humanMessage } from '@/lib/errors.js'
import { money } from '@/lib/format.js'

/**
 * The Phase 2 Settings surfaces (item 16a, surfaces 1-3).
 *
 * ── WHAT THESE THREE CARDS HAVE IN COMMON ───────────────────────────────────
 * Each one edits something a HOMEOWNER eventually sees, and none of it is
 * visible from the contractor's own screens until it is too late to fix. The
 * deposit percentage lands on a signed PDF. The public profile is what a
 * stranded customer reads on an expired link. The boarding state decides
 * whether a pay button works at all.
 *
 * So every card here carries a LIVE PREVIEW of the customer-facing result
 * rather than a form and a save button. That is the whole design brief: a
 * contractor should never have to send themselves a test estimate to find out
 * what their customer will see.
 */

/* ==========================================================================
   1. Deposit terms (D22.1)
   ========================================================================== */

/** Cents to a plain editable dollar string, and back. The API speaks cents
 *  because the column is `_cents`; a contractor types dollars. */
const centsToDollars = cents => (Number(cents ?? 0) / 100).toFixed(2)

function dollarsToCents (text) {
  const n = Number(String(text).replace(/[^0-9.]/g, ''))
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100)
}

export function DepositCard () {
  const { data, isPending, error } = useSettings()
  const save = useUpdateDepositSettings()

  const [percent, setPercent] = React.useState('')
  const [minimum, setMinimum] = React.useState('')
  const [mode, setMode] = React.useState('single')
  const [localError, setLocalError] = React.useState(null)
  const [saved, setSaved] = React.useState(false)

  // Seed the form once the server answers, and re-seed whenever the server
  // value changes underneath us (another tab, a rejected save rolling back).
  React.useEffect(() => {
    if (!data) return
    setPercent(String(data.deposit.deposit_percent))
    setMinimum(centsToDollars(data.deposit.deposit_minimum_cents))
    setMode(data.deposit.default_billing_mode)
  }, [data])

  if (isPending) return <CardSpinner label="Loading deposit settings" />
  if (error) return <Alert tone="danger">{humanMessage(error)}</Alert>

  const floorCents = data.deposit.minimum_floor_cents ?? 0
  const pctNumber = Number(percent)
  const minCents = dollarsToCents(minimum)

  // ── The UI half of D22.7 ──────────────────────────────────────────────────
  // The API refuses this too, and its refusal is what actually guarantees it.
  // This exists because a form that submits and then fails is worse than one
  // that will not submit, and because the contractor is usually typing this
  // once, at setup, with no idea a floor exists.
  const belowFloor = floorCents > 0 && minCents !== null && minCents < floorCents
  const badPercent = !Number.isFinite(pctNumber) || pctNumber < 0 || pctNumber > 100
  const badMinimum = minCents === null
  const blocked = belowFloor || badPercent || badMinimum

  async function submit (event) {
    event.preventDefault()
    setLocalError(null)
    setSaved(false)
    if (blocked) return
    try {
      await save.mutateAsync({
        deposit_percent: pctNumber,
        deposit_minimum_cents: minCents,
        default_billing_mode: mode
      })
      setSaved(true)
    } catch (err) { setLocalError(err) }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="size-4 text-ink-subtle" aria-hidden="true" />
          Deposits
        </CardTitle>
        <p className="mt-1 text-sm/relaxed text-ink-muted">
          How much you ask for up front. This is the default on every new
          estimate, and you can change it on any single one.
        </p>
      </CardHeader>

      <CardBody className="space-y-4">
        {localError && <Alert tone="danger">{humanMessage(localError)}</Alert>}
        {saved && !save.isPending && <Alert tone="success">Saved.</Alert>}

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="deposit-percent"
              label="Deposit percentage"
              type="number" inputMode="decimal" min="0" max="100" step="1"
              value={percent}
              onChange={e => setPercent(e.target.value)}
              error={badPercent && percent !== '' ? 'Enter a number between 0 and 100.' : undefined}
              hint="Applied to the estimate total, including tax."
              data-testid="deposit-percent"
            />

            <Field
              id="deposit-minimum"
              label="Smallest deposit"
              type="text" inputMode="decimal"
              value={minimum}
              onChange={e => setMinimum(e.target.value)}
              error={
                badMinimum
                  ? 'Enter an amount, for example 250.00'
                  : belowFloor
                    ? `The card processor cannot charge less than ${money(floorCents / 100)}, so this has to be at least that.`
                    : undefined
              }
              hint={
                floorCents > 0
                  ? `At least ${money(floorCents / 100)}. Small jobs still need a deposit worth taking.`
                  : 'Stops a percentage from producing a tiny deposit on a small job.'
              }
              data-testid="deposit-minimum"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="billing-mode">How the customer is billed</Label>
            <Select
              id="billing-mode"
              value={mode}
              onChange={e => setMode(e.target.value)}
              data-testid="billing-mode"
            >
              <option value="single">One invoice for the whole job</option>
              <option value="split">Two invoices: deposit now, balance at the end</option>
            </Select>
            <p className="text-sm text-ink-muted">
              {mode === 'single'
                ? 'The customer gets one bill for the full amount. The deposit is a part payment against it.'
                : 'The customer gets a deposit bill now and a separate bill for the balance when the work is done.'}
            </p>
          </div>

          <DepositPreview percent={pctNumber} minimumCents={minCents} mode={mode} />

          <Button type="submit" loading={save.isPending} disabled={blocked} data-testid="save-deposit">
            Save deposit settings
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}

/**
 * The live preview D22.1 asks for, in the contractor's own words: "30% down,
 * $250 minimum". Shown against a worked example, because a percentage on its
 * own does not tell anyone what the customer will actually be asked for, and
 * the interaction between the percentage and the minimum is the part people
 * get wrong.
 */
function DepositPreview ({ percent, minimumCents, mode }) {
  if (!Number.isFinite(percent) || minimumCents === null) return null

  const EXAMPLE_CENTS = 400000 // a $4,000 job, an ordinary size for this trade
  const raw = Math.round(EXAMPLE_CENTS * (percent / 100))
  const deposit = Math.min(Math.max(raw, minimumCents), EXAMPLE_CENTS)
  const minimumWon = deposit > raw
  const balance = EXAMPLE_CENTS - deposit

  return (
    <div className="rounded-md border border-line bg-surface-sunken p-3" data-testid="deposit-preview">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
        What your customer sees
      </p>
      <p className="mt-1.5 text-sm font-medium text-ink" data-testid="deposit-copy">
        {percent}% down{minimumCents > 0 ? `, ${money(minimumCents / 100)} minimum` : ''}
      </p>
      <p className="mt-2 text-sm/relaxed text-ink-muted">
        On a {money(EXAMPLE_CENTS / 100)} job that is{' '}
        <strong className="text-ink">{money(deposit / 100)}</strong> up front
        {balance > 0
          ? <> and {money(balance / 100)} {mode === 'split' ? 'billed separately at the end' : 'left on the invoice'}.</>
          : <> and nothing left to pay.</>}
        {minimumWon && ' Your minimum is what set that, not the percentage.'}
      </p>
    </div>
  )
}

/* ==========================================================================
   2. Public profile (C10)
   ========================================================================== */

/**
 * The six C10 keys, in the order the renderer declares. Kept in the same order
 * here so the form reads top to bottom the way the contact block renders.
 */
const PROFILE_FIELDS = [
  { key: 'business_phone', label: 'Phone', placeholder: '(555) 010-0100', type: 'tel' },
  { key: 'business_email', label: 'Email', placeholder: 'office@yourcompany.com', type: 'email' },
  { key: 'address', label: 'Address', placeholder: '120 Main Street, Springfield' },
  { key: 'license_number', label: 'License number', placeholder: 'ROC 123456' },
  { key: 'hours', label: 'Hours', placeholder: 'Mon to Fri, 7am to 5pm' },
  { key: 'website', label: 'Website', placeholder: 'yourcompany.com', type: 'url' }
]

export function PublicProfileCard () {
  const { data, isPending, error } = useSettings()
  const save = useUpdatePublicProfile()

  const [values, setValues] = React.useState({})
  const [localError, setLocalError] = React.useState(null)
  const [saved, setSaved] = React.useState(false)

  React.useEffect(() => {
    if (!data) return
    setValues({ ...data.public_profile })
  }, [data])

  if (isPending) return <CardSpinner label="Loading public profile" />
  if (error) return <Alert tone="danger">{humanMessage(error)}</Alert>

  const filled = PROFILE_FIELDS.filter(f => (values[f.key] ?? '').trim())
  const isEmpty = filled.length === 0

  async function submit (event) {
    event.preventDefault()
    setLocalError(null)
    setSaved(false)
    try {
      // Send the whole profile. The endpoint REPLACES rather than merges, so
      // a cleared field has to arrive as a cleared field.
      await save.mutateAsync(values)
      setSaved(true)
    } catch (err) { setLocalError(err) }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="size-4 text-ink-subtle" aria-hidden="true" />
          Your public contact details
        </CardTitle>
        <p className="mt-1 text-sm/relaxed text-ink-muted">
          These appear on estimates you send to customers and on the signed PDF.
          Every field is optional. We never publish your personal phone or email.
        </p>
      </CardHeader>

      <CardBody className="space-y-4">
        {/* The consequence, stated where it can still be acted on. D9's expiry
            page is the one place this absence bites, and it bites a homeowner
            who is mid-purchase and cannot reach anyone. */}
        {isEmpty && (
          <Alert tone="warn" title="Nothing published yet" data-testid="profile-empty-warning">
            If a customer opens an estimate link after it has expired, that page
            shows your contact details so they can call you. With nothing here
            it shows no way to reach you, and they hit a dead end.
          </Alert>
        )}

        {localError && <Alert tone="danger">{humanMessage(localError)}</Alert>}
        {saved && !save.isPending && <Alert tone="success">Saved.</Alert>}

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {PROFILE_FIELDS.map(field => (
              <Field
                key={field.key}
                id={`profile-${field.key}`}
                label={field.label}
                type={field.type ?? 'text'}
                placeholder={field.placeholder}
                autoCapitalize={field.type === 'email' || field.type === 'url' ? 'none' : undefined}
                autoCorrect="off"
                value={values[field.key] ?? ''}
                onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                data-testid={`profile-${field.key}`}
              />
            ))}
          </div>

          <ProfilePreview filled={filled} values={values} />

          {/* Item 17, brief §Email sender identity point 3: reply-to is its
              OWN field, separate from both the sending address and the login
              email — and it is deliberately NOT part of the public block
              above. It is never rendered to a customer; it only decides
              where a reply to a sent estimate/invoice lands. Kept on this
              card per the brief's explicit instruction rather than a new
              surface, but visually separated so it does not read as a
              seventh public contact field. */}
          <div className="border-t border-line pt-4">
            <Field
              id="profile-reply_to_email"
              label="Replies to sent emails go to"
              type="email"
              placeholder="you@yourcompany.com"
              autoCapitalize="none"
              autoCorrect="off"
              value={values.reply_to_email ?? ''}
              onChange={e => setValues(v => ({ ...v, reply_to_email: e.target.value }))}
              data-testid="profile-reply_to_email"
            />
            <p className="mt-1.5 text-xs text-ink-subtle">
              Not shown to customers. When someone replies to an estimate or
              invoice you send, it goes here. Defaults to your public email
              above if left blank.
            </p>
          </div>

          <Button type="submit" loading={save.isPending} data-testid="save-profile">
            Save contact details
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}

/**
 * How the block renders to a homeowner.
 *
 * Iterates the DECLARED order and skips absent keys, which is exactly what the
 * assembler and the PDF layout do (item 9's binding convention). Rendering it
 * any other way here would make the preview a lie the first time the two
 * diverged.
 */
function ProfilePreview ({ filled, values }) {
  return (
    <div className="rounded-md border border-line bg-surface-sunken p-3" data-testid="profile-preview">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
        What your customer sees
      </p>
      {filled.length === 0
        ? (
          <p className="mt-1.5 text-sm italic text-ink-subtle">
            No contact details. This block is left off entirely.
          </p>
          )
        : (
          <dl className="mt-1.5 space-y-1">
            {filled.map(field => (
              <div key={field.key} className="flex gap-2 text-sm">
                <dt className="shrink-0 text-ink-muted">{field.label}</dt>
                {/* React escapes this. D29 is enforced at the real output
                    surfaces (the public page and the PDF); this preview
                    inherits the same protection from JSX rather than
                    re-implementing it. */}
                <dd className="min-w-0 break-words text-ink">{values[field.key].trim()}</dd>
              </div>
            ))}
          </dl>
          )}
    </div>
  )
}

/* ==========================================================================
   2b. Sending domain (Phase 2 item 17, brief §Email sender identity)
   ========================================================================== */

const DOMAIN_STATE = {
  unverified: { tone: 'neutral', label: 'Not set up' },
  pending: { tone: 'warn', label: 'Waiting on DNS' },
  verified: { tone: 'success', label: 'Verified' },
  failed: { tone: 'danger', label: 'Failed' }
}

export function SendingDomainCard () {
  const { data: settings } = useSettings()
  const { data, isPending, error } = useSendingDomainStatus()
  const create = useCreateSendingDomain()
  const verify = useVerifySendingDomain()
  const [domain, setDomain] = React.useState('')
  const [localError, setLocalError] = React.useState(null)

  const status = data?.sending_domain?.status ?? settings?.sending_domain?.status ?? 'unverified'
  const domainName = data?.sending_domain?.domain ?? settings?.sending_domain?.domain ?? null
  const records = data?.records ?? []
  const state = DOMAIN_STATE[status] ?? DOMAIN_STATE.unverified

  async function submit (event) {
    event.preventDefault()
    setLocalError(null)
    const value = domain.trim()
    if (!value) return
    try {
      await create.mutateAsync(value)
      setDomain('')
    } catch (err) { setLocalError(err) }
  }

  async function recheck () {
    setLocalError(null)
    try { await verify.mutateAsync() } catch (err) { setLocalError(err) }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="size-4 text-ink-subtle" aria-hidden="true" />
          Sending domain
        </CardTitle>
        <p className="mt-1 text-sm/relaxed text-ink-muted">
          Send estimates and invoices from your own address, like
          invoicing@yourcompany.com, instead of an MBOS address. Until this is
          verified — and any time it is not — email still goes out under an
          MBOS-controlled address with replies routed to you, so sending never
          waits on this.
        </p>
      </CardHeader>

      <CardBody className="space-y-4">
        {(error || localError) && <Alert tone="danger">{humanMessage(localError ?? error)}</Alert>}

        {!domainName
          ? (
            <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="sending-domain-input" className="sr-only">Your domain</Label>
                <Input
                  id="sending-domain-input"
                  value={domain}
                  onChange={e => setDomain(e.target.value)}
                  placeholder="yourcompany.com"
                  autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  inputMode="url"
                  data-testid="sending-domain-input"
                />
              </div>
              <Button type="submit" loading={create.isPending} disabled={!domain.trim()} data-testid="add-sending-domain">
                Add domain
              </Button>
            </form>
            )
          : (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-ink" data-testid="sending-domain-name">{domainName}</span>
                  <Badge tone={state.tone}>{state.label}</Badge>
                </div>
                {status !== 'verified' && (
                  <Button variant="secondary" size="sm" loading={verify.isPending} onClick={recheck} data-testid="verify-sending-domain">
                    <RefreshCw className="size-3.5" aria-hidden="true" /> Re-check
                  </Button>
                )}
              </div>

              {status !== 'verified' && records.length > 0 && (
                <div className="rounded-md border border-line bg-surface-sunken p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
                    Add these records at your domain host
                  </p>
                  <p className="mt-1 text-xs text-ink-muted">
                    Give this to whoever manages your domain's DNS (often the
                    same place your website is hosted). This can take anywhere
                    from a few minutes to a couple of days to take effect —
                    click Re-check once you have added them.
                  </p>
                  <div className="mt-2 space-y-2 overflow-x-auto">
                    {records.map((r, i) => (
                      <div key={i} className="grid grid-cols-[60px_1fr] gap-x-3 gap-y-0.5 rounded border border-line bg-surface p-2 text-xs sm:grid-cols-[60px_140px_1fr]">
                        <span className="font-medium text-ink-muted">{r.record ?? r.type}</span>
                        <span className="font-mono text-ink">{r.name}</span>
                        <span className="min-w-0 break-all font-mono text-ink">{r.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {status === 'failed' && (
                <Alert tone="danger">
                  Verification failed. Double check the records above are added exactly as shown, then re-check.
                </Alert>
              )}
            </>
            )}
      </CardBody>
    </Card>
  )
}

/* ==========================================================================
   3. Payments boarding (§4.8)
   ========================================================================== */

/**
 * Each boarding state, and — the part §4.8 asks for explicitly — what it
 * DEGRADES versus what it BLOCKS. A contractor in underwriting needs to know
 * their business still runs, because the alternative reading of "pending" is
 * "MBOS is broken and I cannot work today".
 */
const BOARDING_STATES = {
  not_connected: {
    tone: 'neutral',
    label: 'Not set up',
    headline: 'You can take payments through MBOS once this is set up.',
    stillWorks: 'Everything else works now: leads, estimates, signatures, invoices, and recording payments you collect yourself.',
    blocked: 'Customers cannot pay you through MBOS yet.'
  },
  pending: {
    tone: 'warn',
    label: 'Being reviewed',
    headline: 'Your application is with the payment provider.',
    stillWorks: 'Nothing is on hold. Keep quoting, signing and invoicing as normal, and record payments you take by cash, check or card terminal.',
    blocked: 'Customers cannot pay online until this is approved. Reviews usually take a few business days.'
  },
  active: {
    tone: 'success',
    label: 'Active',
    headline: 'Payments are switched on.',
    stillWorks: 'Customers can pay you online, and you can still record payments you collect yourself.',
    blocked: null
  },
  disabled: {
    tone: 'danger',
    label: 'Switched off',
    headline: 'Online payments have been switched off for this workspace.',
    stillWorks: 'The rest of MBOS is unaffected. You can still invoice and record payments you collect yourself.',
    blocked: 'Customers cannot pay online. Contact support to find out why and get it restored.'
  }
}

export function PaymentsCard () {
  const { data, isPending, error } = useOnboardingStatus()
  const start = useStartOnboarding()
  const [localError, setLocalError] = React.useState(null)

  if (isPending) return <CardSpinner label="Loading payment status" />

  // A 503 here means OUR deployment has no Maverick configured. That is not
  // the tenant's problem and the card says so rather than showing a broken
  // "Set up payments" button that cannot work.
  if (error) {
    return (
      <Card>
        <CardHeader><CardTitle>Card payments</CardTitle></CardHeader>
        <CardBody>
          <Alert tone="info" title="Not available yet">{humanMessage(error)}</Alert>
        </CardBody>
      </Card>
    )
  }

  const state = BOARDING_STATES[data.maverick_status] ?? BOARDING_STATES.not_connected
  const canStart = ['not_connected', 'pending'].includes(data.maverick_status)

  async function begin () {
    setLocalError(null)
    try {
      const { url } = await start.mutateAsync()
      // A NEW TAB, never an iframe and never this tab. The URL is a bearer
      // credential for the application, and navigating away from the dashboard
      // mid-application is how people lose their place and start a second one.
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) { setLocalError(err) }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="size-4 text-ink-subtle" aria-hidden="true" />
            Card payments
          </CardTitle>
          <p className="mt-1 text-sm/relaxed text-ink-muted">{state.headline}</p>
        </div>
        <Badge tone={state.tone} data-testid="boarding-state">{state.label}</Badge>
      </CardHeader>

      <CardBody className="space-y-4">
        {localError && <Alert tone="danger">{humanMessage(localError)}</Alert>}

        <div className="space-y-2 text-sm/relaxed">
          <p className="flex items-start gap-2 text-ink-muted">
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-success-ink" aria-hidden="true" />
            <span data-testid="boarding-still-works">{state.stillWorks}</span>
          </p>
          {state.blocked && (
            <p className="text-ink-muted" data-testid="boarding-blocked">{state.blocked}</p>
          )}
        </div>

        {/* What is still outstanding on the application. Both lists come from
            Maverick and BOTH ARE EMPTY WHEN MAVERICK IS UNREACHABLE — the API
            degrades rather than failing the whole status read, so an empty
            list here means "nothing to report", not "nothing needed". */}
        {data.still_needed?.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-ink">Still needed</h3>
            <ul className="mt-2 space-y-1.5" data-testid="still-needed">
              {data.still_needed.map((item, i) => (
                <li key={`${item.section}-${item.label}-${i}`} className="flex items-baseline gap-2 text-sm">
                  <span className="text-ink">{item.label}</span>
                  <span className="text-xs text-ink-subtle">{item.section}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.required_documents?.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-ink">Documents</h3>
            <ul className="mt-2 space-y-1.5" data-testid="required-documents">
              {data.required_documents.map((doc, i) => (
                <li key={`${doc.name}-${i}`} className="flex items-center justify-between gap-2 text-sm">
                  <span className="min-w-0 truncate text-ink">{doc.name}</span>
                  <Badge tone={doc.uploaded ? 'success' : doc.action_required ? 'warn' : 'neutral'}>
                    {doc.uploaded ? 'Received' : doc.action_required ? 'Needed' : 'Waiting'}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {canStart && (
          <div className="space-y-2">
            <Button onClick={begin} loading={start.isPending} data-testid="start-boarding">
              {data.maverick_status === 'pending' ? 'Continue your application' : 'Set up payments'}
              <ExternalLink />
            </Button>
            <p className="text-xs text-ink-subtle">
              Opens the payment provider in a new tab. They ask for your business
              details and bank account. MBOS never sees or stores any of it.
            </p>
          </div>
        )}

        {data.application?.declined_reason && (
          <Alert tone="danger" title="Application declined">
            {data.application.declined_reason}
          </Alert>
        )}
      </CardBody>
    </Card>
  )
}

function CardSpinner ({ label }) {
  return (
    <Card>
      <CardBody>
        <div className="flex justify-center py-8"><Spinner label={label} /></div>
      </CardBody>
    </Card>
  )
}
